/**
 * Budget vs. plan vs. actual per goal for the shown week, in rank order.
 * Clicking a plannable row arms that goal: the next block drawn on the grid
 * is for it.
 */
import { clsx } from "clsx";

import type { BudgetRow } from "../../lib/focus";
import { formatMinutes } from "../../lib/focusTime";

export function WeekBudgetPanel({
  rows,
  totals,
  armedGoalId,
  onArm,
}: {
  rows: BudgetRow[];
  totals: { budget_minutes: number; planned_minutes: number; actual_minutes: number };
  armedGoalId: string | null;
  onArm: (goalId: string) => void;
}) {
  return (
    <div className="panel panel--flush">
      <div className="panel-header">
        <h3>Budget</h3>
        <span className="text-xs text-ink-low font-mono">click a goal to draw for it</span>
      </div>
      {rows.length === 0 ? (
        <div className="empty-state">
          <div className="title">No open goals</div>
          <p className="hint">Add a goal in the Goals view, then plan its time here.</p>
        </div>
      ) : (
        <table className="table budget-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Goal</th>
              <th className="num">Budget</th>
              <th className="num">Planned</th>
              <th className="num">Actual</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const armed = row.id === armedGoalId;
              return (
                <tr
                  key={row.id}
                  className={clsx(
                    "budget-row",
                    row.plannable && "is-plannable",
                    armed && "is-armed",
                  )}
                  onClick={row.plannable ? () => onArm(row.id) : undefined}
                  title={row.plannable ? (armed ? "Drawing for this goal" : "Draw blocks for this goal") : "Not plannable – AI team only, or paused"}
                >
                  <td className="font-mono text-ink-low">{row.rank ?? "–"}</td>
                  <td>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className={clsx("budget-armed-dot", armed && "is-on")} aria-hidden />
                      <span className={clsx("truncate", !row.plannable && "text-ink-low")}>{row.title}</span>
                    </div>
                    {row.budget ? (
                      <div className={clsx("budget-meter", row.overPlanned && "is-over")} aria-hidden>
                        <span
                          className="budget-meter-planned"
                          style={{ width: `${Math.min(1, row.plannedShare ?? 0) * 100}%` }}
                        />
                        <span
                          className="budget-meter-actual"
                          style={{ width: `${Math.min(1, row.actualShare ?? 0) * 100}%` }}
                        />
                      </div>
                    ) : null}
                  </td>
                  <td className="num font-mono">{row.budget != null ? formatMinutes(row.budget) : "–"}</td>
                  <td className={clsx("num font-mono", row.overPlanned && "text-yellow-dark")}>
                    {formatMinutes(row.planned)}
                  </td>
                  <td className="num font-mono">{formatMinutes(row.actual)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="budget-total">
              <td />
              <td>Total</td>
              <td className="num font-mono">{formatMinutes(totals.budget_minutes)}</td>
              <td className="num font-mono">{formatMinutes(totals.planned_minutes)}</td>
              <td className="num font-mono">{formatMinutes(totals.actual_minutes)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
